import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

const axios = require('axios').default;
import { toast } from 'react-toastify';
import SponsorshipSettings from '../../../shared/SponsorshipSettings';

import MaskedInput from 'react-text-mask'
import { Button, Plus, TextInput, Toggle, Trash01 } from '@opendate-app/setlist';
import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import MarketingPixelTagsInput from '../../shared/components/MarketingPixelTagsInput';

import CKEditorContainer from '../../CKEditor/CKEditorContainer';
import XMarketingPixelsSettings from './XMarketingPixelsSettings';

const canSubmitForm = (statementDescriptorSuffix, confirm) => {
  return (
    statementDescriptorSuffix
      && statementDescriptorSuffix.length >= 3
      && statementDescriptorSuffix !== confirm.statement_descriptor_suffix
  );
};

const sectionTitleStyle = { "fontSize": "20px" };
const descriptionStyle = { "fontSize": "14px", "color": "var(--sl-text-secondary)" };

var _ = require('lodash');
var debouncedAutoSaveRequirePerTicketCustomChargeType;

const parseFloatFromString = (value) => {
  if(typeof(value) === "number"){
    return value;
  }

  return parseFloat(value.split(",").join(""));
}

const disableCustomChargeTypeEnabled = (customChargeType) => {
  var nameIsBlank = (
    !customChargeType.name
      || customChargeType.name.trim().length === 0
  )

  var defaultAmountIsBlank = (
    !customChargeType.default_amount
      || parseFloatFromString(customChargeType.default_amount) === 0
  )

  return (
    nameIsBlank || defaultAmountIsBlank
  );
}

const updateTicketConfirmationInfo = (csrfToken, team, confirm, ticketConfirmationInfo) => {
  axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

  return axios.patch("/teams/" + team.id + "/confirms/" + confirm.id, {
      confirm: {
        ticket_confirmation_info: ticketConfirmationInfo
      }
    })
    .then(({ data }) => {
      toast.success("Confirmation Email successfully updated", {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true
      });
    })
    .catch((error) => {
      toast.error(error.response.data.join(", "), {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true
      });
    });
};

const Settings = ({
  csrfToken,
  team,
  currentUser,
  confirm,
  statementDescriptorSuffix,
  statementDescriptorSuffixChanged,
  maxDescriptorLength,
  updateStatementDescriptorSuffix,
  requirePerTicketCustomChargeTypes,
  createRequirePerTicketCustomChargeType,
  requirePerTicketCustomChargeTypeChanged,
  deleteRequirePerTicketCustomChargeType,
  autoSaveRequirePerTicketCustomChargeType,
  updateConfirm,
  googleMarketingPixels,
  setGoogleMarketingPixels,
  facebookMarketingPixels,
  setFacebookMarketingPixels,
  tiktokMarketingPixels,
  setTiktokMarketingPixels,
  spotifyMarketingPixels,
  setSpotifyMarketingPixels,
  xMarketingPixels,
  setXMarketingPixels,
  confirmationPageTemplates = []
}) => {
  useEffect(() => {
    debouncedAutoSaveRequirePerTicketCustomChargeType = _.debounce((csrfToken, team, requirePerTicketCustomChargeType) => {
      autoSaveRequirePerTicketCustomChargeType(csrfToken, team, requirePerTicketCustomChargeType);
    }, 300);
  }, [])

  const [confirmationInfo, setConfirmationInfo] = useState(confirm.ticket_confirmation_info || "");

  // Until an event is customized, it inherits the team's default templates;
  // once customized, its explicit selection (confirmation_page_template_ids) wins.
  const enabledConfirmationPageTemplateIds = confirm.confirmation_page_templates_customized
    ? (confirm.confirmation_page_template_ids || [])
    : confirmationPageTemplates.filter((t) => t.enabled_by_default).map((t) => t.id);

  const saveConfirmationPageTemplates = (templateIds) => {
    const previousTemplateSelection = {
      confirmation_page_templates_customized: confirm.confirmation_page_templates_customized,
      confirmation_page_template_ids: confirm.confirmation_page_template_ids
    };
    const updated = Object.assign({}, confirm, {
      confirmation_page_templates_customized: true,
      confirmation_page_template_ids: templateIds
    });

    updateConfirm(updated);

    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    return axios.patch("/teams/" + team.id + "/confirms/" + confirm.id, {
        confirm: {
          confirmation_page_templates_customized: true,
          confirmation_page_template_ids: templateIds
        }
      })
      .then(({ data }) => {
        toast.success("Confirmation page templates updated", {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      })
      .catch((error) => {
        updateConfirm(Object.assign({}, confirm, previousTemplateSelection));
        toast.error("Could not update confirmation page templates", {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };

  const toggleConfirmationPageTemplate = (templateId) => {
    const current = enabledConfirmationPageTemplateIds;
    const next = current.includes(templateId)
      ? current.filter((id) => id !== templateId)
      : [...current, templateId];
    saveConfirmationPageTemplates(next);
  };

  const saveSponsorshipSettings = () => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    const formData = new FormData();

    const confirmParams = {
      show_sponsor_on_digital_tickets: confirm.show_sponsor_on_digital_tickets || false,
      sponsor_name: confirm.sponsor_name || ""
    };

    Object.keys(confirmParams).forEach(key => {
      formData.append(`confirm[${key}]`, confirmParams[key]);
    });

    if (confirm.sponsor_logo && confirm.sponsor_logo instanceof File) {
      formData.append('confirm[sponsor_logo]', confirm.sponsor_logo);
    } else if (confirm.sponsor_logo_url === null) {
      formData.append('confirm[sponsor_logo_url]', '');
    }

    return axios.patch("/teams/" + team.id + "/confirms/" + confirm.id, formData)
      .then(({ data }) => {
        updateConfirm(data);

        toast.success("Sponsorship settings successfully updated", {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };

  return (
    <div className="row" style={{"color": "var(--sl-text-primary)"}}>
      <div className="col-12">
          <form onSubmit={
                  (e) => {
                    e.preventDefault();

                    if(!canSubmitForm(statementDescriptorSuffix, confirm)){
                      return false;
                    }

                    updateStatementDescriptorSuffix(csrfToken, team, confirm, statementDescriptorSuffix);
                  }
                }>
            <div className="row" style={{"marginTop": "27px"}}>
              <div className="col-12">
                <p className="mb-1" style={sectionTitleStyle}>
                  <strong>Credit Card Statement Descriptor</strong>
                </p>
                <p className="m-0" style={descriptionStyle}>
                  Statement descriptors explain charges or payments on bank statements. You can customize how your ticketed event transactions appear on your customer's statements.
                </p>
                <div className="row" style={{"marginTop": "25px"}}>
                  <div className="col col-md-6">
                    <TextInput
                      as={MaskedInput}
                      id="confirm-statement-descriptor-suffix"
                      aria-label="Statement descriptor"
                      leadingText="OD*"
                      hint={(maxDescriptorLength - statementDescriptorSuffix.length) + " characters remaining"}
                      mask={
                        () => {
                          return Array.from(Array(maxDescriptorLength).keys())
                            .map((i) => /[^<>\\'"*]+/);
                        }
                      }
                      guide={false}
                      value={statementDescriptorSuffix || ""}
                      onChange={
                        (e) => {
                          statementDescriptorSuffixChanged(e.target.value);
                        }
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="row" style={{"marginTop": "24px"}}>
              <div className="col-12">
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={!canSubmitForm(statementDescriptorSuffix, confirm)}
                >
                  Save
                </Button>
              </div>
            </div>
          </form>
      </div>
      <div className="col-12" style={{"marginTop": "47px"}}>
        <form onSubmit={
                (e) => {
                  e.preventDefault();

                  var updated = Object.assign({}, confirm, {
                    ticket_confirmation_info: confirmationInfo
                  });

                  updateConfirm(updated);
                  setConfirmationInfo(confirmationInfo);

                  updateTicketConfirmationInfo(csrfToken, team, confirm, confirmationInfo);
                }
              }>
          <p className="mb-1" style={sectionTitleStyle}>
            <strong>Event Details for Confirmation Page and Email</strong>
          </p>
          <p style={{...descriptionStyle, "marginBottom": "25px"}}>
            Add important <strong>event-specific</strong> details or instructions that will be shown within the copy of the confirmation email ticket buyers receive after their purchase. Please note: Any copy added here will be displayed <strong>in addition</strong> to any copy you have setup for the venue.  You can view the confirmation email settings for the venue <a target="_blank" rel="noopener noreferrer" href={`/teams/${team.id}/ticketing_settings`}>here</a>.
          </p>
          {confirmationPageTemplates.length > 0 && (
            <div style={{"marginBottom": "25px"}}>
              <p className="mb-2" style={{"fontSize": "14px"}}>
                <strong>Templates</strong> — turn on any saved templates to show on this event's confirmation screen. Manage templates in your <a target="_blank" rel="noopener noreferrer" href={`/teams/${team.id}/ticketing_settings`}>ticketing settings</a>.
              </p>
              {[...confirmationPageTemplates].sort((a, b) => a.name.localeCompare(b.name)).map((template) =>
                <div className="mb-2" key={template.id}>
                  <Toggle
                    id={`confirmation-page-template-${template.id}`}
                    label={template.name}
                    checked={enabledConfirmationPageTemplateIds.includes(template.id)}
                    onChange={() => toggleConfirmationPageTemplate(template.id)}
                  />
                </div>
              )}
            </div>
          )}
          <CKEditorContainer
            content={confirmationInfo}
            setContent={setConfirmationInfo}
            containerStyle={{ maxWidth: "800px" }}
            allowScriptEmbeds
          />
          <div style={{"marginTop": "24px"}}>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={confirmationInfo == confirm.ticket_confirmation_info}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
      {team.charges_processing_by === "CHARGES_PROCESSING_BY_TICKET" ? (
        <div className="col-12" style={{"marginTop": "47px"}}>
          <p className="mb-1" style={sectionTitleStyle}>
            <strong>Custom Fees & Surcharges</strong>
          </p>
          <p className="m-0" style={descriptionStyle}>
            Add an additional fee or surcharge to all purchases for this event. This will appear as a separate line item under fees during checkout.
          </p>
          <div style={{"marginTop": "25px"}}>
            {requirePerTicketCustomChargeTypes.map((customChargeType, index) =>
              <div key={index}
                  className="d-flex align-items-end"
                  style={{"gap": "12px", "marginBottom": "16px"}}>
                <div style={{"flex": 1}}>
                  <TextInput
                    id={"require-per-ticket-" + customChargeType.id + "-name"}
                    label="Label"
                    value={customChargeType.name || ""}
                    onChange={
                      (e) => {
                        var name = e.target.value;
                        var params = {name: name};

                        if(!name || name.trim().length === 0){
                          params = Object.assign({}, params, {
                            enabled: false
                          });
                        }

                        var updated = Object.assign({}, customChargeType, params);
                        debouncedAutoSaveRequirePerTicketCustomChargeType(csrfToken, team, updated);
                        requirePerTicketCustomChargeTypeChanged(updated);
                      }
                    }
                  />
                </div>
                <div style={{"flex": 1}}>
                  <TextInput
                    as={CurrencyInput}
                    id={"require-per-ticket-" + customChargeType.id + "-default-amount"}
                    label="Amount"
                    leadingText="$"
                    maskOptions={{
                      prefix: "",
                      integerLimit: null
                    }}
                    value={customChargeType.default_amount || ""}
                    onChange={
                      (e) => {
                        var defaultAmount = e.target.value;
                        var params = {default_amount: defaultAmount};

                        if(!defaultAmount || parseFloatFromString(defaultAmount) === 0){
                          params = Object.assign({}, params, {
                            enabled: false
                          });
                        }

                        var updated = Object.assign({}, customChargeType, params);
                        debouncedAutoSaveRequirePerTicketCustomChargeType(csrfToken, team, updated);
                        requirePerTicketCustomChargeTypeChanged(updated);
                      }
                    }
                  />
                </div>
                <div className="d-flex align-items-center"
                    style={{"flex": 1, "gap": "8px", "minHeight": "40px"}}>
                  <Toggle
                    id={"require-per-ticket-" + customChargeType.id + "-enabled"}
                    label="Enabled"
                    checked={customChargeType.enabled || false}
                    disabled={disableCustomChargeTypeEnabled(customChargeType)}
                    onChange={
                      (e) => {
                        var updated = Object.assign({}, customChargeType, {
                          enabled: e.target.checked
                        });

                        debouncedAutoSaveRequirePerTicketCustomChargeType(csrfToken, team, updated);
                        requirePerTicketCustomChargeTypeChanged(updated);
                      }
                    }
                  />
                  {customChargeType.name || customChargeType.default_amount ? (
                    <Button
                      variant="tertiary"
                      size="sm"
                      icon={Trash01}
                      aria-label="Remove fee"
                      onClick={
                        () => {
                          if(window.confirm("Are you sure?")){
                            deleteRequirePerTicketCustomChargeType(csrfToken, team, customChargeType);

                            if(requirePerTicketCustomChargeTypes.length === 1){
                              createRequirePerTicketCustomChargeType(csrfToken, team, confirm);
                            }
                          }
                        }
                      }
                    />
                  ) : null}
                </div>
              </div>
            )}
          </div>
          <Button
            variant="link-color"
            size="sm"
            icon={Plus}
            onClick={() => createRequirePerTicketCustomChargeType(csrfToken, team, confirm)}
          >
            Add Another
          </Button>
        </div>
      ) : null}
      <div className="col-12" style={{"marginTop": "47px"}}>
        <SponsorshipSettings
          entity={confirm}
          onEntityChange={updateConfirm}
          onSave={() => saveSponsorshipSettings()}
        />
      </div>
      <div className="col-12" style={{"marginTop": "47px"}}>
        <p className="mb-1" style={sectionTitleStyle}>
          <strong>Google Analytics</strong>
        </p>
        <div className="row" style={{"marginBottom": "25px"}}>
          <div className="col-12">
            <p className="m-0" style={descriptionStyle}>
              Add Google Analytics tracking IDs (UA-, G-) or Google Tag Manager IDs (GTM-) to track conversions and user behavior for this event.
            </p>
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-8">
            <MarketingPixelTagsInput
              kind="google"
              value={googleMarketingPixels}
              onChange={setGoogleMarketingPixels}
            />
          </div>
        </div>
      </div>
      <div className="col-12" style={{"marginTop": "47px"}}>
        <p className="mb-1" style={sectionTitleStyle}>
          <strong>Facebook Pixel</strong>
        </p>
        <div className="row" style={{"marginBottom": "25px"}}>
          <div className="col-12">
            <p className="m-0" style={descriptionStyle}>
              Add Facebook Pixel IDs to track conversions and user behavior for this event.
            </p>
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-8">
            <MarketingPixelTagsInput
              kind="facebook"
              value={facebookMarketingPixels}
              onChange={setFacebookMarketingPixels}
            />
          </div>
        </div>
      </div>
      <div className="col-12" style={{"marginTop": "47px"}}>
        <p className="mb-1" style={sectionTitleStyle}>
          <strong>TikTok Pixel</strong>
        </p>
        <div className="row" style={{"marginBottom": "25px"}}>
          <div className="col-12">
            <p className="m-0" style={descriptionStyle}>
              Add TikTok Pixel IDs to track conversions and user behavior for this event.
            </p>
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-8">
            <MarketingPixelTagsInput
              kind="tiktok"
              value={tiktokMarketingPixels}
              onChange={setTiktokMarketingPixels}
            />
          </div>
        </div>
      </div>
      <div className="col-12" style={{"marginTop": "47px"}}>
        <p className="mb-1" style={sectionTitleStyle}>
          <strong>Spotify Pixel</strong>
        </p>
        <div className="row" style={{"marginBottom": "25px"}}>
          <div className="col-12">
            <p className="m-0" style={descriptionStyle}>
              Add a Spotify Pixel ID to track conversions and user behavior for this event.
              Spotify supports one pixel per site, so this replaces the venue's Spotify pixel
              for this event rather than firing alongside it.
            </p>
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-8">
            <MarketingPixelTagsInput
              kind="spotify"
              value={spotifyMarketingPixels}
              onChange={setSpotifyMarketingPixels}
            />
          </div>
        </div>
      </div>
      <div className="col-12" style={{"marginTop": "47px", "marginBottom": "200px"}}>
        <p className="mb-1" style={sectionTitleStyle}>
          <strong>X Pixel</strong>
        </p>
        <div className="row" style={{"marginBottom": "25px"}}>
          <div className="col-12">
            <p className="m-0" style={descriptionStyle}>
              Track this event in X. These pixels fire alongside the venue's X pixel. The pixel ID
              tracks page views. For each conversion you want to track, create an event in X Events
              Manager with Define with code and paste its event ID here.
            </p>
          </div>
        </div>
        <XMarketingPixelsSettings
          xMarketingPixels={xMarketingPixels}
          setXMarketingPixels={setXMarketingPixels}
        />
      </div>
    </div>
  );
};

Settings.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  statementDescriptorSuffix: PropTypes.string,
  statementDescriptorSuffixChanged: PropTypes.func.isRequired,
  maxDescriptorLength: PropTypes.number,
  updateStatementDescriptorSuffix: PropTypes.func.isRequired,
  requirePerTicketCustomChargeTypes: PropTypes.array,
  createRequirePerTicketCustomChargeType: PropTypes.func.isRequired,
  requirePerTicketCustomChargeTypeChanged: PropTypes.func.isRequired,
  deleteRequirePerTicketCustomChargeType: PropTypes.func.isRequired,
  autoSaveRequirePerTicketCustomChargeType: PropTypes.func.isRequired,
  updateConfirm: PropTypes.func.isRequired,
  googleMarketingPixels: PropTypes.array,
  setGoogleMarketingPixels: PropTypes.func.isRequired,
  facebookMarketingPixels: PropTypes.array,
  setFacebookMarketingPixels: PropTypes.func.isRequired,
  tiktokMarketingPixels: PropTypes.array,
  setTiktokMarketingPixels: PropTypes.func.isRequired,
  spotifyMarketingPixels: PropTypes.array,
  setSpotifyMarketingPixels: PropTypes.func.isRequired,
  xMarketingPixels: PropTypes.array,
  setXMarketingPixels: PropTypes.func.isRequired,
  confirmationPageTemplates: PropTypes.array
};

export default Settings;
